import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/de';
import {
  useUpdateLogEntryMutation,
  useDeleteLogEntryMutation,
  useCreateLogCommentMutation,
} from '../../redux/api/logbookApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import CommentItem from './CommentItem';

const LogEntryCard = ({ entry }) => {
  const userId = useSelector((state) => state.auth.id);
  const isSuperUser = useSelector((state) => state.auth.isSuperUser);
  const canModify = isSuperUser || entry.created_by === userId;

  const comments = entry.comments || [];
  const commentCount = entry.comment_count ?? comments.length;

  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.body);
  const [commentDraft, setCommentDraft] = useState('');
  const apiErrorToast = useApiErrorToast();

  const [updateEntry, { isLoading: savingEntry }] = useUpdateLogEntryMutation();
  const [deleteEntry] = useDeleteLogEntryMutation();
  const [createComment, { isLoading: postingComment }] =
    useCreateLogCommentMutation();

  const handleSaveEntry = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      await updateEntry({ id: entry.id, body }).unwrap();
      setEditing(false);
      toast.success('Eintrag aktualisiert');
    } catch (err) {
      apiErrorToast(err, 'Speichern fehlgeschlagen');
    }
  };

  const handleDeleteEntry = async () => {
    if (!window.confirm('Eintrag löschen? Kommentare werden mitgelöscht.')) return;
    try {
      await deleteEntry(entry.id).unwrap();
      toast.success('Eintrag gelöscht');
    } catch (err) {
      apiErrorToast(err, 'Löschen fehlgeschlagen');
    }
  };

  const handleAddComment = async () => {
    const body = commentDraft.trim();
    if (!body) return;
    try {
      await createComment({ entry: entry.id, body }).unwrap();
      setCommentDraft('');
      setExpanded(true);
    } catch (err) {
      apiErrorToast(err, 'Kommentar fehlgeschlagen');
    }
  };

  return (
    <div className='card mb-3'>
      <div className='card-body'>
        <div className='d-flex justify-content-between align-items-baseline mb-2'>
          <div className='small text-muted'>
            <i className='fas fa-user me-1' />
            <span className='fw-semibold'>{entry.created_by_username}</span>
            <span className='ms-2'>
              {moment(entry.created_at).locale('de').format('HH:mm')} Uhr
            </span>
          </div>
          {canModify && !editing && (
            <div className='btn-group btn-group-sm'>
              <button
                className='btn btn-link btn-sm text-muted p-0 me-2'
                title='Bearbeiten'
                onClick={() => {
                  setDraft(entry.body);
                  setEditing(true);
                }}
              >
                <i className='fas fa-pen' />
              </button>
              <button
                className='btn btn-link btn-sm text-danger p-0'
                title='Löschen'
                onClick={handleDeleteEntry}
              >
                <i className='fas fa-trash' />
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div>
            <textarea
              className='form-control'
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className='mt-2'>
              <button
                className='btn btn-primary btn-sm me-2'
                disabled={savingEntry}
                onClick={handleSaveEntry}
              >
                Speichern
              </button>
              <button
                className='btn btn-outline-secondary btn-sm'
                onClick={() => setEditing(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{entry.body}</div>
        )}

        {/* Collapsible comments */}
        <div className='mt-3'>
          <button
            className='btn btn-sm btn-link text-decoration-none p-0'
            onClick={() => setExpanded((v) => !v)}
          >
            <i className={`fas fa-chevron-${expanded ? 'down' : 'right'} me-2`} />
            <i className='far fa-comments me-1' />
            {commentCount === 0
              ? 'Kommentieren'
              : `${commentCount} ${commentCount === 1 ? 'Kommentar' : 'Kommentare'}`}
          </button>

          {expanded && (
            <div className='mt-2'>
              {comments.map((c) => (
                <CommentItem key={c.id} comment={c} />
              ))}
              <div className='d-flex align-items-start mt-2 gap-2'>
                <textarea
                  className='form-control form-control-sm'
                  rows={1}
                  placeholder='Kommentar schreiben…'
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      handleAddComment();
                    }
                  }}
                />
                <button
                  className='btn btn-primary btn-sm'
                  disabled={postingComment || !commentDraft.trim()}
                  onClick={handleAddComment}
                >
                  <i className='fas fa-paper-plane' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogEntryCard;
